<template>
  <div class="container">
    <HeadingOne class="-margin-md">収入証明書のご提出</HeadingOne>

    <ErrorsAlert :errors="errors"></ErrorsAlert>

    <NoticeBox v-if="sizeError" class="-error _mb-sm-40 _mb-md-60">
      <NoticeHeader>
        <h3 class="heading">以下の内容をご確認ください。</h3>
      </NoticeHeader>
      <NoticeBody>
        <ul>
          <li>
            登録するファイルの合計サイズが超過しています。
          </li>
        </ul>
      </NoticeBody>
    </NoticeBox>

    <WizardGroup class="_mb-sm-35 _mb-md-50">
      <MainWizard :active-index="1" type="income"></MainWizard>
    </WizardGroup>

    <form @submit.prevent="submitIdentifyFile()">
      <section>
        <template v-if="certificate == '01'">
          <HeadingTwo>源泉徴収票</HeadingTwo>

          <TxtTop></TxtTop>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader>源泉徴収票アップロード時のご確認事項</CardHeader>
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal">
                  源泉徴収票は、給与所得の源泉徴収票のみ受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  最新（前年分）のものをご提出ください。
                </ListItem>
                <ListItem class="-normal">
                  ご提出日が1月の場合は前々年分、12月の場合は当年分の受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  お名前、現在のお勤め先、支払金額が記載されているかをご確認ください。
                </ListItem>
                <ListItem class="-normal">
                  個人番号（マイナンバー）が記載されている場合は、読み取れないように付箋や紙などで隠して撮影してください。
                </ListItem>
                <ListItem class="-normal">
                  手書きの場合は、発行元の押印が必要となります。
                </ListItem>
              </ListGroup>
            </CardBody>
          </CardBox>

          <IncomePoint></IncomePoint>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--1枚目-->
              <div
                v-if="
                  identifyFile.file_data1 &&
                  identifyFile.file_sbt1 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data1"
                  :file-name="identifyFile.file_name1"
                  @onDelete="
                    () => {
                      identifyFile.file_data1 = '';
                      identifyFile.file_name1 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile1"
                  doc-type="income"
                  @onDrop="setImgFile1"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--2枚目-->
              <div
                v-if="
                  identifyFile.file_data2 &&
                  identifyFile.file_sbt2 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data2"
                  :file-name="identifyFile.file_name2"
                  @onDelete="
                    () => {
                      identifyFile.file_data2 = '';
                      identifyFile.file_name2 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile2"
                  doc-type="income"
                  @onDrop="setImgFile2"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--3枚目-->
              <div
                v-if="
                  identifyFile.file_data3 &&
                  identifyFile.file_sbt3 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data3"
                  :file-name="identifyFile.file_name3"
                  @onDelete="
                    () => {
                      identifyFile.file_data3 = '';
                      identifyFile.file_name3 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile3"
                  doc-type="income"
                  @onDrop="setImgFile3"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--4枚目-->
              <div
                v-if="
                  identifyFile.file_data4 &&
                  identifyFile.file_sbt4 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data4"
                  :file-name="identifyFile.file_name4"
                  @onDelete="
                    () => {
                      identifyFile.file_data4 = '';
                      identifyFile.file_name4 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile4"
                  doc-type="income"
                  @onDrop="setImgFile4"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--5枚目-->
              <div
                v-if="
                  identifyFile.file_data5 &&
                  identifyFile.file_sbt5 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data5"
                  :file-name="identifyFile.file_name5"
                  @onDelete="
                    () => {
                      identifyFile.file_data5 = '';
                      identifyFile.file_name5 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile5"
                  doc-type="income"
                  @onDrop="setImgFile5"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="certificate == '02'">
          <HeadingTwo>給与明細書</HeadingTwo>

          <TxtTop>
            <p>
              給与明細書のほか、賞与明細書も含め最大5枚までご選択いただけます。
            </p>
          </TxtTop>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader>給与明細書アップロード時のご確認事項</CardHeader>
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal">
                  連続した直近2か月分をご提出ください。
                </ListItem>
                <ListItem class="-normal">
                  賞与明細書がある場合は、直近1年分をあわせてご提出ください。</ListItem
                >
                <ListItem class="-normal"
                  >お名前、現在のお勤め先、支給総額が記載されているかをご確認ください。</ListItem
                >
                <ListItem class="-normal"
                  >手書きの場合は、発行元の押印が必要となります。</ListItem
                >
              </ListGroup>
            </CardBody>
          </CardBox>

          <IncomePoint></IncomePoint>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--1枚目-->
              <div
                v-if="
                  identifyFile.file_data1 &&
                  identifyFile.file_sbt1 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data1"
                  :file-name="identifyFile.file_name1"
                  @onDelete="
                    () => {
                      identifyFile.file_data1 = '';
                      identifyFile.file_name1 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile1"
                  doc-type="income"
                  @onDrop="setImgFile1"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--2枚目-->
              <div
                v-if="
                  identifyFile.file_data2 &&
                  identifyFile.file_sbt2 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data2"
                  :file-name="identifyFile.file_name2"
                  @onDelete="
                    () => {
                      identifyFile.file_data2 = '';
                      identifyFile.file_name2 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile2"
                  doc-type="income"
                  @onDrop="setImgFile2"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--3枚目-->
              <div
                v-if="
                  identifyFile.file_data3 &&
                  identifyFile.file_sbt3 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data3"
                  :file-name="identifyFile.file_name3"
                  @onDelete="
                    () => {
                      identifyFile.file_data3 = '';
                      identifyFile.file_name3 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile3"
                  doc-type="income"
                  @onDrop="setImgFile3"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--4枚目-->
              <div
                v-if="
                  identifyFile.file_data4 &&
                  identifyFile.file_sbt4 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data4"
                  :file-name="identifyFile.file_name4"
                  @onDelete="
                    () => {
                      identifyFile.file_data4 = '';
                      identifyFile.file_name4 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile4"
                  doc-type="income"
                  @onDrop="setImgFile4"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--5枚目-->
              <div
                v-if="
                  identifyFile.file_data5 &&
                  identifyFile.file_sbt5 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data5"
                  :file-name="identifyFile.file_name5"
                  @onDelete="
                    () => {
                      identifyFile.file_data5 = '';
                      identifyFile.file_name5 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile5"
                  doc-type="income"
                  @onDrop="setImgFile5"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="certificate == '03'">
          <HeadingTwo>所得（課税）証明書</HeadingTwo>

          <TxtTop></TxtTop>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader
              >所得（課税）証明書アップロード時のご確認事項</CardHeader
            >
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal">
                  最新（前年分）のものをご提出ください。
                </ListItem>
                <ListItem class="-normal">
                  ご提出日が1月から6月の場合は、前々年分の受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  お名前、収入額、所得額が記載されているかをご確認ください。
                </ListItem>
                <ListItem class="-normal">
                  発行元が地方自治体の場合を除き、発行元の記載、および、押印が必要となります。
                </ListItem>
                <ListItem class="-normal">
                  事業所得や不動産所得がある方など、給与所得以外の所得がある場合は、所得額の内訳が記載されているかをご確認ください。
                </ListItem>
              </ListGroup>
            </CardBody>
          </CardBox>

          <IncomePoint></IncomePoint>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--1枚目-->
              <div
                v-if="
                  identifyFile.file_data1 &&
                  identifyFile.file_sbt1 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data1"
                  :file-name="identifyFile.file_name1"
                  @onDelete="
                    () => {
                      identifyFile.file_data1 = '';
                      identifyFile.file_name1 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile1"
                  doc-type="income"
                  @onDrop="setImgFile1"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--2枚目-->
              <div
                v-if="
                  identifyFile.file_data2 &&
                  identifyFile.file_sbt2 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data2"
                  :file-name="identifyFile.file_name2"
                  @onDelete="
                    () => {
                      identifyFile.file_data2 = '';
                      identifyFile.file_name2 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile2"
                  doc-type="income"
                  @onDrop="setImgFile2"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--3枚目-->
              <div
                v-if="
                  identifyFile.file_data3 &&
                  identifyFile.file_sbt3 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data3"
                  :file-name="identifyFile.file_name3"
                  @onDelete="
                    () => {
                      identifyFile.file_data3 = '';
                      identifyFile.file_name3 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile3"
                  doc-type="income"
                  @onDrop="setImgFile3"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--4枚目-->
              <div
                v-if="
                  identifyFile.file_data4 &&
                  identifyFile.file_sbt4 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data4"
                  :file-name="identifyFile.file_name4"
                  @onDelete="
                    () => {
                      identifyFile.file_data4 = '';
                      identifyFile.file_name4 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile4"
                  doc-type="income"
                  @onDrop="setImgFile4"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--5枚目-->
              <div
                v-if="
                  identifyFile.file_data5 &&
                  identifyFile.file_sbt5 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data5"
                  :file-name="identifyFile.file_name5"
                  @onDelete="
                    () => {
                      identifyFile.file_data5 = '';
                      identifyFile.file_name5 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile5"
                  doc-type="income"
                  @onDrop="setImgFile5"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="certificate == '04'">
          <HeadingTwo>納税通知書（住民税決定通知書）</HeadingTwo>

          <TxtTop></TxtTop>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader
              >納税通知書(住民税決定通知書)アップロード時のご確認事項</CardHeader
            >
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal">
                  納税通知書は、住民税決定通知書（市民税、県民税の特別徴収税額の決定・変更通知書など）のみ受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  最新（前年分）のものをご提出ください。
                </ListItem>
                <ListItem class="-normal">
                  ご提出日が1月から6月の場合は、前々年分の受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  お名前、収入額、所得額、発行元の記載、および、押印があるかをご確認ください。
                </ListItem>
                <ListItem class="-normal">
                  事業所得や不動産所得がある方など、給与所得以外の所得がある場合は、所得額の内訳が記載されているかをご確認ください。
                </ListItem>
              </ListGroup>
            </CardBody>
          </CardBox>

          <IncomePoint></IncomePoint>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--1枚目-->
              <div
                v-if="
                  identifyFile.file_data1 &&
                  identifyFile.file_sbt1 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data1"
                  :file-name="identifyFile.file_name1"
                  @onDelete="
                    () => {
                      identifyFile.file_data1 = '';
                      identifyFile.file_name1 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile1"
                  doc-type="income"
                  @onDrop="setImgFile1"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--2枚目-->
              <div
                v-if="
                  identifyFile.file_data2 &&
                  identifyFile.file_sbt2 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data2"
                  :file-name="identifyFile.file_name2"
                  @onDelete="
                    () => {
                      identifyFile.file_data2 = '';
                      identifyFile.file_name2 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile2"
                  doc-type="income"
                  @onDrop="setImgFile2"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--3枚目-->
              <div
                v-if="
                  identifyFile.file_data3 &&
                  identifyFile.file_sbt3 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data3"
                  :file-name="identifyFile.file_name3"
                  @onDelete="
                    () => {
                      identifyFile.file_data3 = '';
                      identifyFile.file_name3 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile3"
                  doc-type="income"
                  @onDrop="setImgFile3"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--4枚目-->
              <div
                v-if="
                  identifyFile.file_data4 &&
                  identifyFile.file_sbt4 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data4"
                  :file-name="identifyFile.file_name4"
                  @onDelete="
                    () => {
                      identifyFile.file_data4 = '';
                      identifyFile.file_name4 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile4"
                  doc-type="income"
                  @onDrop="setImgFile4"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--5枚目-->
              <div
                v-if="
                  identifyFile.file_data5 &&
                  identifyFile.file_sbt5 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data5"
                  :file-name="identifyFile.file_name5"
                  @onDelete="
                    () => {
                      identifyFile.file_data5 = '';
                      identifyFile.file_name5 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile5"
                  doc-type="income"
                  @onDrop="setImgFile5"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="certificate == '05'">
          <HeadingTwo>確定申告書</HeadingTwo>

          <TxtTop></TxtTop>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader>確定申告書アップロード時のご確認事項</CardHeader>
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal"
                  >最新（前年分）のものをご提出ください。</ListItem
                >
                <ListItem class="-normal"
                  >ご提出日が1月から3月の場合は、前々年分の受付が可能です。</ListItem
                >
                <ListItem class="-normal"
                  >お名前、収入額、所得額、税務署/税理士事務所等の名称の記載、または、押印があるかをご確認ください。</ListItem
                >
                <ListItem class="-normal"
                  >電子申告の場合は、「受付日時」「受付番号」、または、「電子申告完了済」の文言が記載されているかをご確認ください。</ListItem
                >
                <ListItem class="-normal"
                  >個人番号（マイナンバー）が記載されている場合は、読み取れないように付箋や紙などで隠して撮影してください。</ListItem
                >
              </ListGroup>
            </CardBody>
          </CardBox>

          <IncomePoint></IncomePoint>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--1枚目-->
              <div
                v-if="
                  identifyFile.file_data1 &&
                  identifyFile.file_sbt1 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data1"
                  :file-name="identifyFile.file_name1"
                  @onDelete="
                    () => {
                      identifyFile.file_data1 = '';
                      identifyFile.file_name1 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile1"
                  doc-type="income"
                  @onDrop="setImgFile1"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--2枚目-->
              <div
                v-if="
                  identifyFile.file_data2 &&
                  identifyFile.file_sbt2 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data2"
                  :file-name="identifyFile.file_name2"
                  @onDelete="
                    () => {
                      identifyFile.file_data2 = '';
                      identifyFile.file_name2 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile2"
                  doc-type="income"
                  @onDrop="setImgFile2"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--3枚目-->
              <div
                v-if="
                  identifyFile.file_data3 &&
                  identifyFile.file_sbt3 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data3"
                  :file-name="identifyFile.file_name3"
                  @onDelete="
                    () => {
                      identifyFile.file_data3 = '';
                      identifyFile.file_name3 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile3"
                  doc-type="income"
                  @onDrop="setImgFile3"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--4枚目-->
              <div
                v-if="
                  identifyFile.file_data4 &&
                  identifyFile.file_sbt4 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data4"
                  :file-name="identifyFile.file_name4"
                  @onDelete="
                    () => {
                      identifyFile.file_data4 = '';
                      identifyFile.file_name4 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile4"
                  doc-type="income"
                  @onDrop="setImgFile4"
                  @error="showError"
                />
              </div>
            </div>
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--5枚目-->
              <div
                v-if="
                  identifyFile.file_data5 &&
                  identifyFile.file_sbt5 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data5"
                  :file-name="identifyFile.file_name5"
                  @onDelete="
                    () => {
                      identifyFile.file_data5 = '';
                      identifyFile.file_name5 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile5"
                  doc-type="income"
                  @onDrop="setImgFile5"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>
      </section>

      <section v-if="pension == '02'">
        <template v-if="pensionDoc == '01'">
          <HeadingTwo>年金通知書</HeadingTwo>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader>年金通知書アップロード時のご確認事項</CardHeader>
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal"
                  >最新（当年分）のものをご提出ください。</ListItem
                >
                <ListItem class="-normal"
                  >ご提出日が1月から6月の場合は、前年分の受付が可能です。</ListItem
                >
                <ListItem class="-normal"
                  >お名前、年金の種類、年金額、発行元の記載、および、押印があるかをご確認ください。</ListItem
                >
                <ListItem class="-normal"
                  >発行元は、公的機関、企業年金連合会、企業年金基金、「○○年金基金」と記載されているもののみ受付可能です。</ListItem
                >
                <ListItem class="-normal"
                  >基礎年金番号が記載されている場合は、読み取れないように付箋や紙などで隠して撮影してください。</ListItem
                >
              </ListGroup>
            </CardBody>
          </CardBox>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--6枚目-->
              <div
                v-if="
                  identifyFile.file_data6 &&
                  identifyFile.file_sbt6 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data6"
                  :file-name="identifyFile.file_name6"
                  @onDelete="
                    () => {
                      identifyFile.file_data6 = '';
                      identifyFile.file_name6 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile6"
                  doc-type="income"
                  @onDrop="setImgFile6"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>

        <template v-if="pensionDoc == '02'">
          <HeadingTwo>年金証書</HeadingTwo>

          <CardBox class="_mb-sm-40 _mb-md-60">
            <CardHeader>年金証書アップロード時のご確認事項</CardHeader>
            <CardBody>
              <p>以下をご確認ください。</p>
              <ListGroup class="-normal">
                <ListItem class="-normal">
                  年金証書は、裁定通知書欄（決定通知書欄）の記載があるもののみ受付が可能です。
                </ListItem>
                <ListItem class="-normal">
                  最新のものをご提出ください。
                </ListItem>
                <ListItem class="-normal">
                  お名前、年金の種類、年金額、支給開始年月、発行元の記載、および、押印があるかをご確認ください。
                </ListItem>
                <ListItem class="-normal">
                  発行元は、公的機関、企業年金連合会、企業年金基金、「○○年金基金」と記載されているもののみ受付可能です。
                </ListItem>
                <ListItem class="-normal">
                  基礎年金番号が記載されている場合は、読み取れないように付箋や紙などで隠して撮影してください。
                </ListItem>
              </ListGroup>
            </CardBody>
          </CardBox>

          <div class="row">
            <div class="col-12 col-md-6 _mb-sm-30 _mb-md-50">
              <!--6枚目-->
              <div
                v-if="
                  identifyFile.file_data6 &&
                  identifyFile.file_sbt6 === getFileSbt()
                "
              >
                <UploadedImage
                  :file-data="identifyFile.file_data6"
                  :file-name="identifyFile.file_name6"
                  @onDelete="
                    () => {
                      identifyFile.file_data6 = '';
                      identifyFile.file_name6 = '';
                    }
                  "
                />
              </div>
              <div v-else>
                <UploadImage
                  id="setImgFile6"
                  doc-type="income"
                  @onDrop="setImgFile6"
                  @error="showError"
                />
              </div>
            </div>
          </div>
        </template>
      </section>

      <section>
        <BtngroupDouble class="-margin-lg">
          <template #main>
            <BtnMain class="-main" @click.prevent="submitIdentifyFile()">
              確認へ進む
            </BtnMain>
          </template>
          <template #back>
            <BtnBack class="-back -sm" @click.prevent="goBack()">
              戻る
            </BtnBack>
          </template>
        </BtngroupDouble>
      </section>
    </form>
  </div>
  <!-- container -->
</template>

<script>
import { goToErrorPage, scrollTop } from "@/utils/Other";
import { initCredentials } from "@/utils/Auth";
import {
  validateIdentifyFileLambdaInvoke,
  getUserDataLambdaInvoke,
} from "@/utils/LambdaHelper";
import { INCOME_CERTIFICATE_IMAGE_AT } from "@/constants/constant";

import HeadingOne from "@/components/heading/HeadingOne";
import HeadingTwo from "@/components/heading/HeadingTwo";
import MainWizard from "@/components/wizard/MainWizard";
import WizardGroup from "@/components/wizard/WizardGroup";
import BtngroupDouble from "@/components/button/BtngroupDouble";
import BtnMain from "@/components/button/BtnMain";
import BtnBack from "@/components/button/BtnBack";
import UploadImage from "@/components/upload/UploadImage";
import UploadedImage from "@/components/upload/UploadedImage";
import ListGroup from "@/components/list/ListGroup";
import ListItem from "@/components/list/ListItem";
import CardHeader from "@/components/card/CardHeader";
import CardBody from "@/components/card/CardBody";
import CardBox from "@/components/card/CardBox";
import IncomePoint from "@/components/upload/IncomePoint";
import ErrorsAlert from "@/components/alert/ErrorsAlert";
import TxtTop from "@/components/upload/TxtTop";
import NoticeBox from "@/components/box/NoticeBox";
import NoticeHeader from "@/components/box/NoticeHeader";
import NoticeBody from "@/components/box/NoticeBody";

export default {
  name: "IncomeSelect",

  components: {
    TxtTop,
    ErrorsAlert,
    IncomePoint,
    CardBox,
    CardBody,
    CardHeader,
    ListItem,
    ListGroup,
    UploadImage,
    UploadedImage,
    BtnBack,
    BtnMain,
    BtngroupDouble,
    WizardGroup,
    MainWizard,
    HeadingOne,
    HeadingTwo,
    NoticeHeader,
    NoticeBox,
    NoticeBody,
  },
  props: {
    previousPageId: { type: String, default: "" },
    nextPageId: { type: String, default: "" },
  },
  data() {
    return {
      errors: null,
      isDisabled: false,
      certificate: this.$route.query.certificate,
      pension: this.$route.query.pension,
      pensionDoc: this.$route.query.pensionDoc,
      files: true,
      sizeError: false,
      identifyFile: {
        certificate: this.$route.query.certificate,
        pension: this.$route.query.pension,
        pensionDoc: this.$route.query.pensionDoc,
        processing_type: "2",
        file_name1: "",
        file_data1: "",
        file_sbt1: "",
        file_name2: "",
        file_data2: "",
        file_sbt2: "",
        file_name3: "",
        file_data3: "",
        file_sbt3: "",
        file_name4: "",
        file_data4: "",
        file_sbt4: "",
        file_name5: "",
        file_data5: "",
        file_sbt5: "",
        file_name6: "",
        file_data6: "",
        file_sbt6: "",
      },
    };
  },
  created() {
    this.checkQueryParam();
  },
  mounted() {
    try {
      if (window.$cookies.get(INCOME_CERTIFICATE_IMAGE_AT)) {
        goToErrorPage("Image upload prohibited");
      }
    } catch (e) {
      goToErrorPage(e);
    }
    try {
      getUserDataLambdaInvoke(this.files)
        .then((res) => {
          
          for (let key in res) {
            if (Object.prototype.hasOwnProperty.call(this.identifyFile, key)) {
              // if (this.certificate !== "02") {
              //   if (
              //     [
              //       "file_name2",
              //       "file_data2",
              //       "file_sbt2",
              //       "file_name3",
              //       "file_data3",
              //       "file_sbt3",
              //       "file_name4",
              //       "file_data4",
              //       "file_sbt4",
              //       "file_name5",
              //       "file_data5",
              //       "file_sbt5",
              //     ].indexOf(key) > -1
              //   ) {
              //     continue;
              //   }
              // }
              if (this.pension !== "02") {
                if (
                  ["file_name6", "file_data6", "file_sbt6"].indexOf(key) > -1
                ) {
                  continue;
                }
              }
              this.identifyFile[key] = res[key];
            }
          }
        })
        .catch((error) => {
          goToErrorPage(error);
        });
    } catch (e) {
      goToErrorPage(e);
    }
  },
  methods: {
    submitIdentifyFile() {
      const bytes =
        this.identifyFile.file_data1.length +
        this.identifyFile.file_data2.length +
        this.identifyFile.file_data3.length +
        this.identifyFile.file_data4.length +
        this.identifyFile.file_data5.length +
        this.identifyFile.file_data6.length;

      this.sizeError = bytes > 6144000; // 6000KB
      if (this.sizeError) {
        scrollTop();
        return;
      }

      try {
        if (this.isDisabled) {
          return;
        }
        this.checkQueryParam();
        this.isDisabled = true;
        this.errors = {};
        this.identifyFile.processing_type = "2";
        initCredentials()
          .then(() => {
            validateIdentifyFileLambdaInvoke(this.identifyFile)
              .then((res) => {
                this.errors = {};
                if (res.errors) {
                  for (let e in res.errors) {
                    let key = e;
                    if (e.indexOf("file_data") !== -1) {
                      if (e === "file_data1") {
                        key += "_ce" + this.certificate;
                      } else if (e === "file_data6") {
                        key += "_ce" + this.pensionDoc;
                      }
                      this.errors[key] = res.errors[e];
                      if (this.errors[key].length > 0) {
                        this.errors[key][0] = this.errors[key][0].replace(
                          "empty",
                          "unselect"
                        );
                      }
                    }
                  }
                  scrollTop();
                  this.isDisabled = false;
                } else {
                  let queryParam = {
                    certificate: this.certificate || "00",
                    pension: this.pension || "01",
                    pensionDoc: this.pensionDoc || "00",
                  };
                  this.$router.push({
                    name: this.nextPageId,
                    query: queryParam,
                  });
                }
              })
              .catch((error) => {
                goToErrorPage(error);
              });
          })
          .catch((error) => {
            goToErrorPage(error);
          });
      } catch (e) {
        goToErrorPage(e);
      }
    },
    setImgFile1(fileData, fileName) {
      this.identifyFile.file_data1 = fileData;
      this.identifyFile.file_name1 = fileName;
      this.identifyFile.file_sbt1 = this.getFileSbt();
    },
    setImgFile2(fileData, fileName) {
      this.identifyFile.file_data2 = fileData;
      this.identifyFile.file_name2 = fileName;
      this.identifyFile.file_sbt2 = this.getFileSbt();
    },
    setImgFile3(fileData, fileName) {
      this.identifyFile.file_data3 = fileData;
      this.identifyFile.file_name3 = fileName;
      this.identifyFile.file_sbt3 = this.getFileSbt();
    },
    setImgFile4(fileData, fileName) {
      this.identifyFile.file_data4 = fileData;
      this.identifyFile.file_name4 = fileName;
      this.identifyFile.file_sbt4 = this.getFileSbt();
    },
    setImgFile5(fileData, fileName) {
      this.identifyFile.file_data5 = fileData;
      this.identifyFile.file_name5 = fileName;
      this.identifyFile.file_sbt5 = this.getFileSbt();
    },
    setImgFile6(fileData, fileName) {
      this.identifyFile.file_data6 = fileData;
      this.identifyFile.file_name6 = fileName;
      this.identifyFile.file_sbt6 = this.getFileSbt();
    },
    showError(error) {
      this.errors = error;
    },
    goBack() {
      let queryParam = {
        certificate: this.certificate || "00",
        pension: this.pension || "01",
        pensionDoc: this.pensionDoc || "00",
      };
      this.$router.push({ name: this.previousPageId, query: queryParam });
    },
    checkQueryParam() {
      try {
        if (
          window.__PRERENDER_INJECTED &&
          window.__PRERENDER_INJECTED.isPrerendering
        ) {
          // ビルド時の静的ファイル生成時はチェックしない
          return;
        }
        if (
          ["01", "02", "03", "04", "05"].indexOf(
            this.$route.query.certificate
          ) === -1
        ) {
          this.$router.push({ name: this.previousPageId });
        }
        if (["01", "02"].indexOf(this.$route.query.pension) === -1) {
          this.$router.push({ name: this.previousPageId });
        }
        if (["02"].indexOf(this.$route.query.pension) === 0) {
          if (["01", "02"].indexOf(this.$route.query.pensionDoc) === -1) {
            this.$router.push({ name: this.previousPageId });
          }
        }
      } catch (e) {
        goToErrorPage(e);
      }
    },
    getFileSbt() {
      // (01: 運転免許書 02: 運転経歴証明書 03: パスポート 04: 健康保険被保険証書 05: マイナンバーカード 06: 在留カード 07: 特別永住者証明書 08: 住民票の写し 09: 公的書類 10: 収入証明書)
      return "10";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/stylesheets/styles";

:deep(.point-box) {
  display: flex;
  flex-wrap: wrap;
  > .point-img {
    width: 100%;
    @include mq-up(md) {
      width: 50%;
    }
    > .txt01 {
      width: 100%;
      display: flex;
      align-items: center;
      font-weight: bold;
      img {
        width: 20px;
        @include mq-up(md) {
          width: 30px;
        }
      }
    }
    img {
      width: 50%;
      @include mq-down(md) {
        width: calc(50% - 2px);
      }
      @include mq-up(md) {
        /*max-width: 287px;*/
        width: 100%;
      }
    }
  }
}
</style>
